import React from "react";
import { Tag } from "antd";
import { formatAmount, formatHistoryDate } from "./historyUtils";
import type { LegacyHistoryItem, LegacyHistoryPayment } from "./historyUtils";

interface LegacyHistoryPanelProps {
  historyItems: LegacyHistoryItem[];
  historyPayments: LegacyHistoryPayment[];
}

interface LegacySectionProps {
  title: string;
  children: React.ReactNode;
}

const LegacySection: React.FC<LegacySectionProps> = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded overflow-hidden">
    <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
      <span className="text-xs font-semibold text-gray-600">{title}</span>
    </div>
    <div className="divide-y divide-gray-100">{children}</div>
  </div>
);

const RemovedItemRow: React.FC<{ item: LegacyHistoryItem }> = ({ item }) => (
  <div className="px-3 py-2 flex justify-between items-center text-xs">
    <div>
      <div className="font-semibold text-gray-800">
        {item.product_name || "Unknown item"}
      </div>
      <div className="text-gray-400 mt-0.5">
        SKU: {item.sku_code || "N/A"} | Batch: {item.batch_no || "N/A"}
      </div>
    </div>
    <div className="text-right">
      <Tag color="orange">
        {item.quantity} {item.unit || ""}
      </Tag>
      <div className="text-gray-400 mt-0.5">
        {formatHistoryDate(item.deleted_at)}
      </div>
    </div>
  </div>
);

const ReversedPaymentRow: React.FC<{ payment: LegacyHistoryPayment }> = ({
  payment,
}) => (
  <div className="px-3 py-2 flex justify-between items-center text-xs">
    <span className="font-semibold capitalize text-gray-800">
      {payment.payment_method_name || "Payment"}
    </span>
    <div className="text-right">
      <div className="font-bold text-gray-700">
        LKR {formatAmount(payment.amount)}
      </div>
      <div className="text-gray-400 mt-0.5">
        {formatHistoryDate(payment.deleted_at)}
      </div>
    </div>
  </div>
);

export const LegacyHistoryPanel: React.FC<LegacyHistoryPanelProps> = ({
  historyItems,
  historyPayments,
}) => {
  const hasItems = historyItems.length > 0;
  const hasPayments = historyPayments.length > 0;

  return (
    <div className="flex flex-col gap-3">
      {hasItems && (
        <LegacySection title="Removed / Replaced Items">
          {historyItems.map((item) => (
            <RemovedItemRow key={item.id} item={item} />
          ))}
        </LegacySection>
      )}

      {hasPayments && (
        <LegacySection title="Replaced / Reversed Payments">
          {historyPayments.map((payment) => (
            <ReversedPaymentRow key={payment.id} payment={payment} />
          ))}
        </LegacySection>
      )}
    </div>
  );
};
